<script setup lang="ts">
import { components } from '~/slices'

const { client } = usePrismic()

const { data: page } = await useAsyncData('contact', async () => {
  try {
    return await client.getByUID('page', 'contact')
  } catch {
    return null
  }
})

useDocumentSeo(page)

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  honeypot: '',
  terms: false,
})

const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submitError = ref('')

function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  const w = window as unknown as { gtag?: (...args: unknown[]) => void }
  if (typeof w.gtag === 'function') {
    w.gtag('event', name, { flow: 'contact_form', ...params })
  }
}

async function handleSubmit() {
  submitError.value = ''
  isSubmitting.value = true
  trackEvent('contact_submit_attempt', { subject: form.subject })
  try {
    const res = await $fetch<{ ok: boolean; error?: string }>('/api/contact-request', {
      method: 'POST',
      body: {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        honeypot: form.honeypot,
        terms: form.terms,
      },
    })
    if (res.ok) {
      isSubmitted.value = true
      trackEvent('contact_submit_success', { subject: form.subject })
    } else {
      submitError.value = res.error === 'validation'
        ? 'Niet alle verplichte velden zijn ingevuld.'
        : res.error === 'rate_limited'
          ? 'Je hebt recent al meerdere berichten gestuurd. Probeer het later opnieuw.'
          : 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
      trackEvent('contact_submit_error', { reason: res.error || 'unknown' })
    }
  } catch {
    submitError.value = 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
    trackEvent('contact_submit_error', { reason: 'request_failed' })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <main>
    <SliceZone v-if="page" :slices="page.data.slices ?? []" :components="components" />

    <section class="pa-quote-page">
      <div class="pa-container">
        <div class="pa-quote-page__grid">
          <div class="pa-quote-page__form-wrapper">
            <div v-if="isSubmitted" class="pa-quote-page__success">
              <h2>Bedankt voor je bericht!</h2>
              <p>We hebben je vraag ontvangen en nemen zo snel mogelijk contact met je op.</p>
            </div>

            <form v-else class="pa-quote-page__form" novalidate @submit.prevent="handleSubmit">
              <p class="pa-quote-page__form-required">Velden met * zijn verplicht</p>

              <!-- Honeypot — must stay empty -->
              <div class="pa-quote-page__form-honeypot" aria-hidden="true">
                <label>Website (laat leeg)</label>
                <input
                    v-model="form.honeypot"
                    type="text"
                    name="website"
                    tabindex="-1"
                    autocomplete="off"
                >
              </div>

              <h3 class="pa-quote-page__form-section">Wie ben je?</h3>
              <div class="pa-quote-page__form-row">
                <div class="pa-quote-page__form-field">
                  <input v-model="form.firstName" type="text" placeholder="Voornaam*" required>
                </div>
                <div class="pa-quote-page__form-field">
                  <input v-model="form.lastName" type="text" placeholder="Achternaam*" required>
                </div>
              </div>

              <h3 class="pa-quote-page__form-section">Hoe kunnen we je bereiken?</h3>
              <div class="pa-quote-page__form-row">
                <div class="pa-quote-page__form-field">
                  <input v-model="form.email" type="email" placeholder="E-mailadres*" required>
                </div>
                <div class="pa-quote-page__form-field">
                  <input v-model="form.phone" type="tel" placeholder="Telefoonnummer*" required>
                </div>
              </div>

              <h3 class="pa-quote-page__form-section">Waar gaat het over?</h3>
              <div class="pa-quote-page__form-field pa-quote-page__form-field--full">
                <input v-model="form.subject" type="text" placeholder="Onderwerp">
              </div>
              <div class="pa-quote-page__form-field pa-quote-page__form-field--full">
                <textarea v-model="form.message" placeholder="Je bericht*" rows="6" required />
              </div>

              <label class="pa-quote-page__form-checkbox">
                <input v-model="form.terms" type="checkbox" required>
                <span>Ja, ik ga akkoord met de algemene voorwaarden</span>
              </label>

              <button
                  type="submit"
                  class="pa-btn pa-btn--primary"
                  :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Verzenden…' : 'Verstuur bericht' }}
                <span v-if="!isSubmitting" aria-hidden="true">&rarr;</span>
              </button>

              <p v-if="submitError" class="pa-quote-page__form-error">{{ submitError }}</p>
            </form>
          </div>

          <ContactInfoSidebar />
        </div>
      </div>
    </section>
  </main>
</template>
